import React from "react";
import { Link as GatsbyLink } from "gatsby";
import { ContentSection, HeaderSection } from "../docs/sections";
import { DocPageMeta, Header } from "../docs";
import { Paragraph, TextContainer, Card, Content, Grid, Ul } from "../../lib/src";

export const Meta: DocPageMeta = {
	category: "Writing",
};

export default (): JSX.Element => {
	return (
		<>
			<HeaderSection
				title="Get started"
				subTitle="This page lists the most important things you’ll need to do to start writing and designing UI content for Siteimprove experiences."
			/>
			<ContentSection>
				<TextContainer article>
					<Header.H2>Guidelines and standards</Header.H2>
					<Paragraph>
						Writing and content standards guide teams in creating clear, consistent copy for
						Siteimprove products. These general principles work together with component-specific
						guidelines (e.g.,{" "}
						<GatsbyLink to="/lib/components/Navigation/Tabs#writing">Tabs {">"} Writing</GatsbyLink>
						) to cover both broad writing strategy and detailed implementation.
					</Paragraph>
					<br></br>
					<Grid flush equalHeight>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Grammar and mechanics</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Adherence to these standards helps position Siteimprove as a reliable and
											trustworthy source of knowledge.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="/Writing/grammar-and-mechanics/">View guideline</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Tone of voice</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											The way we write is an expression of who we are. It’s a reflection of our
											brand and the values we uphold as a company.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="Writing/tone-of-voice">View guideline</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
						<Grid.Section size="third">
							<Card>
								<Card.Header>Word list</Card.Header>
								<Content>
									<TextContainer article>
										<Paragraph>
											Using words intentionally helps our users navigate. This guide contains
											approved words and how to use them in our UI.
										</Paragraph>
									</TextContainer>
								</Content>
								<Card.Footer>
									<Content justifyContent="center">
										<GatsbyLink to="Writing/word-list">View guideline</GatsbyLink>
									</Content>
								</Card.Footer>
							</Card>
						</Grid.Section>
					</Grid>
					<Header.H2>UX writing process</Header.H2>
					<Paragraph>
						Our UX writing process maintains copy quality while streamlining collaboration between
						design, product, and localization teams. UX Designers lead all copy decisions to ensure
						clear, consistent communication that serves our users.
					</Paragraph>
					<Header.H3>Phase 1: Copy creation</Header.H3>
					<Ul
						items={[
							<>UX Designer collaborates with Product Manager on copy during design phase.</>,
							<>UX Designer uses writing guidelines checklist before finalizing copy.</>,
							<>UX Designer documents final copy decisions in Figma.</>,
						]}
					/>
					<Header.H3>Phase 2: Implementation & review</Header.H3>
					<Ul
						items={[
							<>
								Developer creates translation keys and English strings using Figma as source of
								truth.
							</>,
							<>Developer provides localization team with strings for review.</>,
							<>Localization team reviews keys against Figma file and prepares for translation.</>,
						]}
					/>
					<Header.H3>Phase 3: Sign-off & translation</Header.H3>
					<Ul
						items={[
							<>Localization team asks UX Designer to review translation strings.</>,
							<>
								UX Designer does final copy audit comparing keys to their original design intent in
								Figma and makes edits directly in localization tool.
							</>,
							<>
								UX Designer signs off on translation keys (Jira status moved to "Signed-off by UX").
							</>,
							<>
								Localization team sends strings for translation (Jira status moved to "In
								Translation").
							</>,
							<>
								Localization team updates translation keys with translated content and notifies
								Product Manager and UX Designer (Jira status moved to "Done").
							</>,
						]}
					/>
					<Header.H2>Get help</Header.H2>
					<Paragraph>
						Questions about how translation keys work or when to involve the localization team?
						Request a demonstration to see the full process from string creation to translated
						implementation. Contact the localization team on the{" "}
						<a
							href="https://siteimprove.slack.com/archives/C03481R95"
							target="_blank"
							rel="noreferrer"
						>
							translations
						</a>{" "}
						Slack channel for a hands-on walkthrough.
					</Paragraph>
				</TextContainer>
			</ContentSection>
		</>
	);
};
